import ace from 'ace-builds'
import 'ace-builds/src-noconflict/mode-markdown'
import 'ace-builds/src-noconflict/ext-language_tools'
import 'ace-builds/src-noconflict/ext-searchbox'
import 'ace-builds/src-noconflict/theme-monokai'
import 'ace-builds/src-noconflict/theme-tomorrow'
import type { EditorAdapter, EditorPosition, EditorSelection, EditorOptions } from '../editor-adapter'

function toAceRow(line: number): number {
  return Math.max(0, line - 1)
}

export function createAceAdapter(container: HTMLElement, options: EditorOptions): Promise<EditorAdapter> {
  const changeListeners: Array<(value: string) => void> = []
  const selectionListeners: Array<(selection: EditorSelection) => void> = []

  return new Promise((resolve) => {
    const editor = ace.edit(container, {
      value: options.value ?? '',
      mode: 'ace/mode/markdown',
      theme: options.theme === 'dark' ? 'ace/theme/monokai' : 'ace/theme/tomorrow',
      readOnly: options.readOnly ?? false,
      showPrintMargin: false,
      tabSize: options.tabSize ?? 4,
      fontSize: options.fontSize ?? 14,
      wrap: options.wordWrap !== false,
      wrapBehavioursEnabled: true,
      autoScrollEditorIntoView: true,
    })
    editor.setOptions({ enableBasicAutocompletion: options.autoComplete ?? true })

    if (options.placeholder) {
      editor.container.setAttribute('data-placeholder', options.placeholder)
    }

    const getPositionFromOffset = (offset: number): EditorPosition => {
      const pos = editor.session.doc.indexToPosition(offset)
      return { line: pos.row + 1, column: pos.column, offset }
    }

    const getOffsetFromPosition = (pos: EditorPosition): number => {
      return editor.session.doc.positionToIndex({ row: toAceRow(pos.line), column: pos.column })
    }

    editor.session.on('change', () => {
      changeListeners.forEach((l) => l(editor.getValue()))
    })

    editor.selection.on('changeCursor', () => {
      const range = editor.selection.getRange()
      selectionListeners.forEach((l) => {
        const start = getPositionFromOffset(editor.session.doc.positionToIndex(range.start))
        const end = getPositionFromOffset(editor.session.doc.positionToIndex(range.end))
        l({
          start,
          end,
          isEmpty: editor.selection.isEmpty(),
          text: editor.session.getTextRange(range),
        })
      })
    })

    const adapter: EditorAdapter = {
      getValue() { return editor.getValue() },

      setValue(value: string) { editor.setValue(value); editor.clearSelection() },

      getSelection(): EditorSelection {
        const range = editor.selection.getRange()
        const start = getPositionFromOffset(editor.session.doc.positionToIndex(range.start))
        const end = getPositionFromOffset(editor.session.doc.positionToIndex(range.end))
        return { start, end, isEmpty: editor.selection.isEmpty(), text: editor.session.getTextRange(range) }
      },

      setSelection(start: EditorPosition, end?: EditorPosition) {
        const e = end ?? start
        editor.selection.setRange({
          start: { row: toAceRow(start.line), column: start.column },
          end: { row: toAceRow(e.line), column: e.column },
        } as any)
        editor.focus()
      },

      replaceSelection(text: string) {
        editor.session.replace(editor.selection.getRange(), text)
      },

      insertText(text: string) {
        editor.insert(text)
      },

      wrapSelection(prefix: string, suffix: string) {
        const selected = this.getSelection()
        const text = selected.text
        if (selected.isEmpty) {
          const linePos = editor.session.doc.indexToPosition(selected.start.offset)
          editor.session.insert(linePos, prefix + text + suffix)
          editor.moveCursorTo(linePos.row, linePos.column + prefix.length)
        } else {
          const range = editor.selection.getRange()
          editor.session.replace(range, prefix + text + suffix)
          editor.selection.setRange({
            start: { row: range.start.row, column: range.start.column + prefix.length },
            end: { row: range.end.row, column: range.end.column + suffix.length },
          } as any)
        }
        editor.focus()
      },

      wrapLine(prefix: string, suffix: string) {
        const sel = editor.selection.getRange()
        for (let row = sel.start.row; row <= sel.end.row; row++) {
          const cur = editor.session.getLine(row)
          const indent = /^\s*/.exec(cur)?.[0] ?? ''
          editor.session.replace({ start: { row, column: 0 }, end: { row, column: cur.length } }, indent + prefix + cur.slice(indent.length) + suffix)
        }
        editor.focus()
      },

      toggleLinePrefix(prefix: string) {
        const sel = editor.selection.getRange()
        let allApplied = true
        for (let row = sel.start.row; row <= sel.end.row; row++) {
          const cur = editor.session.getLine(row)
          const indent = /^[ \t]*/.exec(cur)?.[0] ?? ''
          if (!cur.slice(indent.length).startsWith(prefix)) { allApplied = false; break }
        }
        for (let row = sel.end.row; row >= sel.start.row; row--) {
          const cur = editor.session.getLine(row)
          const indent = /^[ \t]*/.exec(cur)?.[0] ?? ''
          if (allApplied) {
            editor.session.replace({ start: { row, column: 0 }, end: { row, column: indent.length + prefix.length } }, indent)
          } else {
            editor.session.insert({ row, column: indent.length }, prefix)
          }
        }
        editor.focus()
      },

      indentLines() {
        const sel = editor.selection.getRange()
        for (let row = sel.end.row; row >= sel.start.row; row--) {
          editor.session.insert({ row, column: 0 }, '  ')
        }
        editor.focus()
      },

      outdentLines() {
        const sel = editor.selection.getRange()
        for (let row = sel.end.row; row >= sel.start.row; row--) {
          const cur = editor.session.getLine(row)
          const m = /^ {0,4}|\t/.exec(cur)
          if (m) editor.session.replace({ start: { row, column: 0 }, end: { row, column: m[0].length } }, '')
        }
        editor.focus()
      },

      insertLink() {
        const selected = this.getSelection().text || '链接文字'
        this.replaceSelection(`[${selected}](https://)`)
        editor.focus()
      },

      insertImage() {
        const selected = this.getSelection().text || '图片描述'
        this.replaceSelection(`![${selected}](https://)`)
        editor.focus()
      },

      insertTable() {
        const table = '| 列1 | 列2 | 列3 |\n| --- | --- | --- |\n| 单元格 | 单元格 | 单元格 |\n| 单元格 | 单元格 | 单元格 |'
        editor.insert(table)
        editor.focus()
      },

      insertHorizontalRule() {
        editor.insert('\n---\n')
        editor.focus()
      },

      insertFootnote() {
        const count = editor.getValue().match(/\[\^(\d+)\]/g)?.length ?? 0
        editor.insert(` [^${count + 1}]`)
        editor.focus()
      },

      undo() { editor.undo() },
      redo() { editor.redo() },

      canUndo(): boolean { return editor.session.getUndoManager().hasUndo() },
      canRedo(): boolean { return editor.session.getUndoManager().hasRedo() },

      focus() { editor.focus() },

      onChange(callback: (value: string) => void) {
        changeListeners.push(callback)
        return () => { const i = changeListeners.indexOf(callback); if (i >= 0) changeListeners.splice(i, 1) }
      },

      onSelectionChange(callback: (selection: EditorSelection) => void) {
        selectionListeners.push(callback)
        return () => { const i = selectionListeners.indexOf(callback); if (i >= 0) selectionListeners.splice(i, 1) }
      },

      getLineCount() { return editor.session.getLength() },

      getLineContent(line: number) {
        try { return editor.session.getLine(toAceRow(line)) } catch { return '' }
      },

      setLineContent(line: number, content: string) {
        editor.session.replace({ start: { row: toAceRow(line), column: 0 }, end: { row: toAceRow(line), column: this.getLineContent(line).length } }, content)
      },

      isSelectionWrapped(prefix: string, suffix: string) {
        const text = this.getSelection().text
        return text.startsWith(prefix) && text.endsWith(suffix)
      },

      dispose() {
        changeListeners.length = 0
        selectionListeners.length = 0
        editor.destroy()
        container.innerHTML = ''
      },
    }

    resolve(adapter)
  })
}